// login.jsx - PyK7VM Sign-In
// Compiled to dist/login.js by build.mjs. Loaded by login.php.

const { useState, useEffect, useRef } = React;
const { Aurora } = window;

// ─── CLEAN SVG ARROW ICON ───────────────────────────────────────────────────
function SvgArrowRight({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// ─── THEME TOGGLE BUTTON ───────────────────────────────────────────────────────
function ThemeToggleBtn({ style = {}, showLabel = true, className = '' }) {
  const [theme, setTheme] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const handleThemeChange = (e) => {
      setTheme(e.detail?.theme || (document.documentElement.classList.contains('light') ? 'light' : 'dark'));
    };
    window.addEventListener('pk7vm-theme-change', handleThemeChange);
    return () => window.removeEventListener('pk7vm-theme-change', handleThemeChange);
  }, []);

  const toggle = () => {
    if (window.__PK7VM_THEME__?.toggleTheme) {
      const next = window.__PK7VM_THEME__.toggleTheme();
      setTheme(next);
    } else {
      const isLight = document.documentElement.classList.contains('light');
      const next = isLight ? 'dark' : 'light';
      if (next === 'light') {
        document.documentElement.classList.add('light');
        document.documentElement.setAttribute('data-theme', 'light');
      } else {
        document.documentElement.classList.remove('light');
        document.documentElement.setAttribute('data-theme', 'dark');
      }
      try { localStorage.setItem('pk7vm_theme', next); } catch (e) {}
      setTheme(next);
    }
  };

  const isLight = theme === 'light';

  return (
    <button
      type="button"
      onClick={toggle}
      className={`pk7vm-nav-theme-btn ${className}`}
      aria-label={isLight ? 'Switch to Dark mode' : 'Switch to Light mode'}
      title={isLight ? 'Switch to Dark mode (Ctrl+Shift+L)' : 'Switch to Light mode (Ctrl+Shift+L)'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(255, 255, 255, 0.05)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
        borderRadius: 999,
        padding: showLabel ? '7px 14px 7px 11px' : '7px 10px',
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        transition: 'all .2s ease',
        minHeight: 36,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
    >
      <span className="pk7vm-theme-icon-wrap" aria-hidden="true" style={{ width: 16, height: 16, position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg className="pk7vm-theme-icon pk7vm-theme-icon-sun" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', opacity: isLight ? 0 : 1, transform: isLight ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)', transition: 'transform .35s, opacity .3s' }}>
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
        <svg className="pk7vm-theme-icon pk7vm-theme-icon-moon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', opacity: isLight ? 1 : 0, transform: isLight ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)', transition: 'transform .35s, opacity .3s' }}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
      </span>
      {showLabel && <span className="pk7vm-theme-toggle-label">{isLight ? 'Dark' : 'Light'}</span>}
    </button>
  );
}

// ─── AUTH NAVIGATION HEADER ───────────────────────────────────────────────────
function AuthNav({ rightLabel, rightHref }) {
  const [hovered, setHovered] = useState(false);

  return (
    <nav
      className="auth-nav"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 clamp(16px, 4vw, 32px)',
        height: 64,
        background: 'rgba(8,8,13,0.7)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <a
        href="index.php"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          fontWeight: 600,
          letterSpacing: -0.4,
          fontSize: 16,
          textDecoration: 'none',
          color: 'var(--text)',
          minHeight: 44,
        }}
      >
        <img
          src="favicon.ico"
          alt="PyK7VM Logo"
          aria-hidden="true"
          style={{ width: 24, height: 24, borderRadius: 6 }}
        />
        <span>PyK7VM</span>
      </a>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <ThemeToggleBtn />
        <a
          href={rightHref}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13.5,
            fontWeight: 500,
            color: hovered ? 'var(--text)' : 'var(--text-muted)',
            textDecoration: 'none',
            transition: 'color .2s ease',
            minHeight: 44,
          }}
        >
          <span>{rightLabel}</span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              transform: hovered ? 'translateX(3px)' : 'none',
              transition: 'transform .2s ease',
            }}
          >
            <SvgArrowRight size={13} color="currentColor" />
          </span>
        </a>
      </div>
    </nav>
  );
}

// ─── TEXT INPUT FIELD ─────────────────────────────────────────────────────────
function InputField({ id, type = 'text', label, placeholder, value, onChange, disabled, mono, autoComplete }) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ marginBottom: 18 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 12.5,
          fontWeight: 500,
          color: 'var(--text-dim)',
          marginBottom: 7,
          letterSpacing: 0.1,
        }}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        autoComplete={autoComplete}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="auth-input"
        style={{
          width: '100%',
          padding: '12px 14px',
          background: 'rgba(6,6,12,0.85)',
          border: `1px solid ${focused ? 'rgba(59,130,246,0.6)' : 'var(--border)'}`,
          boxShadow: focused ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
          color: 'var(--text)',
          borderRadius: 12,
          fontFamily: mono ? 'var(--font-code)' : 'var(--font-ui)',
          fontSize: 14,
          outline: 'none',
          transition: 'border-color .2s ease, box-shadow .2s ease',
          WebkitAppearance: 'none',
          minHeight: 44,
        }}
      />
    </div>
  );
}

// ─── PASSWORD INPUT FIELD ─────────────────────────────────────────────────────
function PasswordField({ id, label, placeholder, value, onChange, disabled, show, onToggleShow }) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ marginBottom: 20 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 12.5,
          fontWeight: 500,
          color: 'var(--text-dim)',
          marginBottom: 7,
          letterSpacing: 0.1,
        }}
      >
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          autoComplete="current-password"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="auth-input"
          style={{
            width: '100%',
            padding: '12px 42px 12px 14px',
            background: 'rgba(6,6,12,0.85)',
            border: `1px solid ${focused ? 'rgba(59,130,246,0.6)' : 'var(--border)'}`,
            boxShadow: focused ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
            color: 'var(--text)',
            borderRadius: 12,
            fontFamily: 'var(--font-ui)',
            fontSize: 14,
            outline: 'none',
            transition: 'border-color .2s ease, box-shadow .2s ease',
            WebkitAppearance: 'none',
            minHeight: 44,
          }}
        />
        <button
          type="button"
          onClick={onToggleShow}
          tabIndex={-1}
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color .2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── LOGIN APPLICATION ROOT ───────────────────────────────────────────────────
function LoginApp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaMode, setMfaMode] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const totpRef = useRef(null);

  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Enter') {
        submit();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  async function submit() {
    if (mfaMode) {
      await verifyTotp();
      return;
    }
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const res = await fetch('api/auth/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Invalid credentials.');
        setLoading(false);
        return;
      }
      if (data.totp_required) {
        setMfaMode(true);
        setLoading(false);
        setTimeout(() => totpRef.current?.focus(), 60);
        return;
      }
      window.location.href = 'forge.php';
    } catch (e) {
      setError('Network connection error. Please try again.');
      setLoading(false);
    }
  }

  async function verifyTotp() {
    if (!totpCode) {
      setError('Enter your 6-digit authenticator or backup recovery code.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const res = await fetch('api/auth/totp_verify.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: totpCode }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Invalid authenticator code.');
        setLoading(false);
        return;
      }
      window.location.href = 'forge.php';
    } catch (e) {
      setError('Network connection error. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.35 }}>
        <Aurora palette={{ a: '#1e40af', b: '#3b82f6', c: '#08080d' }} />
      </div>

      <AuthNav rightLabel="Create account" rightHref="register.php" />

      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(80px, 12vw, 110px) clamp(16px, 4vw, 24px) clamp(30px, 6vw, 60px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ width: '100%', maxWidth: 420 }}>
          
          {/* Header Title */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1
              style={{
                fontSize: 'clamp(26px, 4vw, 32px)',
                fontWeight: 500,
                letterSpacing: -1.2,
                lineHeight: 1.15,
                color: 'var(--text)',
                marginBottom: 8,
              }}
            >
              {mfaMode ? 'Two-Factor Verification' : 'Welcome back.'}
            </h1>
            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(13.5px, 1.8vw, 14.5px)', margin: 0, lineHeight: 1.5 }}>
              {mfaMode
                ? 'Enter your 6-digit authenticator or backup code.'
                : 'Sign in to access The Forge.'}
            </p>
          </div>

          {/* Centered Glassmorphic Card */}
          <div
            className="auth-card"
            style={{
              background: 'rgba(245,241,232,0.015)',
              border: '1px solid var(--border-strong)',
              borderRadius: 22,
              padding: 'clamp(24px, 4vw, 36px) clamp(18px, 4vw, 32px) 28px',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5), inset 0 0 20px rgba(59,130,246,0.03)',
            }}
          >
            <style>{`
              html.light .auth-card {
                background: #ffffff !important;
                border-color: rgba(15, 23, 42, 0.08) !important;
                box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.04) !important;
              }
              html.light .auth-input {
                background: #ffffff !important;
                color: #0f172a !important;
                border-color: rgba(15, 23, 42, 0.14) !important;
              }
              html.light .auth-nav {
                background: rgba(255, 255, 255, 0.9) !important;
                border-bottom-color: rgba(15, 23, 42, 0.08) !important;
              }
            `}</style>
            {/* Error Notification */}
            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  color: '#f87171',
                  padding: '12px 14px',
                  borderRadius: 12,
                  fontSize: 13.5,
                  fontWeight: 500,
                  marginBottom: 20,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {!mfaMode ? (
              /* Step 1: Credentials Form */
              <div>
                <InputField
                  id="f-email"
                  type="email"
                  label="Email address"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  mono
                />

                <PasswordField
                  id="f-password"
                  label="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  show={showPw}
                  onToggleShow={() => setShowPw((s) => !s)}
                />

                <button
                  onClick={submit}
                  disabled={loading}
                  onMouseEnter={() => setBtnHover(true)}
                  onMouseLeave={() => setBtnHover(false)}
                  style={{
                    width: '100%',
                    padding: '13px',
                    marginTop: 6,
                    background: 'var(--text)',
                    opacity: loading ? 0.7 : btnHover ? 0.92 : 1,
                    color: 'var(--bg)',
                    border: 'none',
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 14.5,
                    cursor: loading ? 'default' : 'pointer',
                    letterSpacing: -0.2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                    transform: btnHover && !loading ? 'translateY(-1px)' : 'none',
                    transition: 'all .2s ease',
                    minHeight: 46,
                  }}
                >
                  {loading ? 'Authenticating…' : 'Sign In'}
                </button>

                <p style={{ textAlign: 'center', marginTop: 24, fontSize: 13.5, color: 'var(--text-muted)' }}>
                  Don't have an account?{' '}
                  <a
                    href="register.php"
                    style={{
                      color: 'var(--brand-soft)',
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                  >
                    Create one free
                  </a>
                </p>
              </div>
            ) : (
              /* Step 2: 2FA / TOTP Verification Form */
              <div>
                <div style={{ marginBottom: 20 }}>
                  <label
                    htmlFor="f-totp"
                    style={{
                      display: 'block',
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: 'var(--text-dim)',
                      marginBottom: 7,
                      letterSpacing: 0.1,
                    }}
                  >
                    Authenticator Code
                  </label>
                  <input
                    ref={totpRef}
                    id="f-totp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456 or backup code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      background: 'var(--surface)',
                      border: '1px solid var(--border-strong)',
                      color: 'var(--text)',
                      borderRadius: 12,
                      fontFamily: 'var(--font-code)',
                      fontSize: 16,
                      textAlign: 'center',
                      letterSpacing: 4,
                      outline: 'none',
                      minHeight: 44,
                    }}
                  />
                </div>

                <button
                  onClick={submit}
                  disabled={loading}
                  onMouseEnter={() => setBtnHover(true)}
                  onMouseLeave={() => setBtnHover(false)}
                  style={{
                    width: '100%',
                    padding: '13px',
                    background: 'var(--text)',
                    opacity: loading ? 0.7 : btnHover ? 0.92 : 1,
                    color: 'var(--bg)',
                    border: 'none',
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 14.5,
                    cursor: loading ? 'default' : 'pointer',
                    letterSpacing: -0.2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                    transform: btnHover && !loading ? 'translateY(-1px)' : 'none',
                    transition: 'all .2s ease',
                    minHeight: 46,
                  }}
                >
                  {loading ? 'Verifying…' : 'Verify Code'}
                </button>

                <div style={{ textAlign: 'center', marginTop: 18 }}>
                  <button
                    onClick={() => {
                      setMfaMode(false);
                      setError('');
                      setTotpCode('');
                    }}
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      transition: 'color .2s ease',
                      minHeight: 40,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <line x1="19" y1="12" x2="5" y2="12" />
                      <polyline points="12 19 5 12 12 5" />
                    </svg>
                    <span>Back to credentials</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(LoginApp));
